/**
 * Один поток микрофона на всё приложение.
 *
 * Зачем: `getUserMedia` — единственный способ получить звук, и браузер
 * решает сам, показывать ли окно разрешения. Chrome и Firefox запоминают
 * ответ, но Safari на iPhone (особенно приложение с экрана «Домой») может
 * спрашивать на каждый вызов. Раньше каждая фраза открывала новый поток и
 * закрывала его — отсюда окно на каждое касание. Теперь поток открывается
 * один раз и переиспользуется:
 *
 *   `acquireMic()` — взять поток (открытый или открыть); `release()` — вернуть,
 *   дорожки не останавливаются;
 *   поток живёт, пока кто-то держит аренду, и ещё `IDLE_MS` после этого —
 *   следующее касание в дрилле или игре обходится без `getUserMedia`;
 *   `holdMic()` — звонок держит поток весь разговор;
 *   скрытая вкладка / уход со страницы — поток закрывается (индикатор
 *   микрофона не горит зря, iOS всё равно гасит его в фоне).
 *
 * Открытие — синхронно в касании (`primeMicrophone`), как требует iOS:
 * первый запрос уходит без единого `await`.
 */

import { prepareCaptureSession, trackCapture } from '../lib/audioSession';
import { reportMicDropped, reportMicFailure, reportMicRecovered } from './micDiagnostics';

/** Одни настройки звука для всех путей записи (поток, запись на сервер, перебивание). */
export const MIC_CONSTRAINTS: NonNullable<Parameters<MediaDevices['getUserMedia']>[0]> = {
  audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
};

/** Сколько держать поток после последнего использования. */
const IDLE_MS = 60_000;

/**
 * Микрофон уже давали на этом устройстве. Нужен там, где Permissions API нет
 * или он врёт (Safari на iOS отвечает `prompt` и после разрешения): с флагом
 * `getUserMedia` зовётся сразу, без объяснения; `NotAllowedError` флаг снимает.
 * Стирается при выходе (`api/auth.ts`).
 */
export const MIC_GRANTED_KEY = 'micPermissionGranted';

export function micGrantedBefore(): boolean {
  try {
    return localStorage.getItem(MIC_GRANTED_KEY) === 'true';
  } catch {
    return false;
  }
}

function setGrantedFlag(granted: boolean): void {
  try {
    if (granted) localStorage.setItem(MIC_GRANTED_KEY, 'true');
    else localStorage.removeItem(MIC_GRANTED_KEY);
  } catch {
    // Хранилище закрыто — без флага просто спросим объяснением.
  }
}

function isDenial(error: unknown): boolean {
  const name = typeof error === 'object' && error !== null ? (error as { name?: unknown }).name : undefined;
  return name === 'NotAllowedError' || name === 'SecurityError';
}

let shared: MediaStream | null = null;
let opening: Promise<MediaStream> | null = null;
/** Записи, которые сейчас читают поток. */
let leases = 0;
/** Удержания (звонок): поток не закрывается по простою, но системному распознаванию его можно освободить. */
let holds = 0;
const users = (): number => leases + holds;
let idleTimer: number | undefined;

function isLive(stream: MediaStream): boolean {
  return stream.getAudioTracks().some((track) => track.readyState === 'live');
}

/** Дорожка жива, но система её приглушила (iOS: прерывание аудиосессии) — приходит тишина. */
function isMuted(stream: MediaStream): boolean {
  return stream.getAudioTracks().some((track) => track.readyState === 'live' && track.muted);
}

/* ── Здоровье потока во время звонка ────────────────────────────── */

export type MicHealthEvent =
  /** Поток пропал и сам не вернулся: нужно касание («Попробовать снова»). */
  | { readonly type: 'dropped'; readonly reason: string }
  /** Поток переоткрыт: текущее прослушивание стоит начать заново. */
  | { readonly type: 'restored'; readonly reason: string };

const healthListeners = new Set<(event: MicHealthEvent) => void>();

/** Подписка на обрывы и восстановление (экран звонка). */
export function onMicHealth(listener: (event: MicHealthEvent) => void): () => void {
  healthListeners.add(listener);
  return () => healthListeners.delete(listener);
}

function emitHealth(event: MicHealthEvent): void {
  for (const listener of healthListeners) listener(event);
}

/** Когда звонок начал держать поток — для метрики «на какой минуте оборвалось». */
let heldSince = 0;
let reopening: Promise<MediaStream> | null = null;
/** Ждём, пока iOS вернёт приглушённую дорожку, прежде чем открывать заново. */
const UNMUTE_WAIT_MS = 1000;

/**
 * Поток оборвался (система закончила дорожку, приглушила её, страница
 * вернулась из фона), пока звонок его держит, — открыть заново. Успех —
 * `restored`; отказ (iOS после прерывания часто требует касания) — `dropped`.
 */
function reopenHeld(reason: string): Promise<MediaStream> | null {
  if (holds === 0) return null;
  if (reopening !== null) return reopening;
  reportMicDropped(reason, heldSince === 0 ? 0 : Date.now() - heldSince);
  const old = shared;
  shared = null;
  old?.getTracks().forEach((track) => track.stop());
  const request = openShared('reopen');
  reopening = request;
  request.then(
    () => {
      reportMicRecovered('reopen', 1);
      emitHealth({ type: 'restored', reason });
    },
    () => emitHealth({ type: 'dropped', reason }),
  );
  const clear = () => {
    if (reopening === request) reopening = null;
  };
  request.then(clear, clear);
  return request;
}

/** Перед прослушиванием: приглушённая дорожка — подождать, затем открыть заново. */
async function healthyShared(stage: string): Promise<MediaStream> {
  const live = liveShared();
  if (live !== null && isMuted(live)) {
    const unmuted = await new Promise<boolean>((resolve) => {
      const tracks = live.getAudioTracks();
      const done = (value: boolean) => {
        window.clearTimeout(timer);
        tracks.forEach((track) => track.removeEventListener('unmute', onUnmute));
        resolve(value);
      };
      const onUnmute = () => done(true);
      const timer = window.setTimeout(() => done(!isMuted(live)), UNMUTE_WAIT_MS);
      tracks.forEach((track) => track.addEventListener('unmute', onUnmute));
    });
    if (!unmuted && shared === live) {
      if (holds > 0) return reopenHeld('muted') ?? openShared(stage);
      closeMic();
    }
  }
  return openShared(stage);
}

/** Общий поток, если он открыт и жив. */
function liveShared(): MediaStream | null {
  return shared !== null && isLive(shared) ? shared : null;
}

function isSessionConflict(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'InvalidStateError';
}

/** Закрыть общий поток (простой, скрытая вкладка). */
export function closeMic(): void {
  window.clearTimeout(idleTimer);
  const stream = shared;
  shared = null;
  stream?.getTracks().forEach((track) => track.stop());
}

function scheduleIdleClose(): void {
  window.clearTimeout(idleTimer);
  if (users() > 0 || shared === null) return;
  idleTimer = window.setTimeout(() => {
    if (users() === 0) closeMic();
  }, IDLE_MS);
}

function adopt(stream: MediaStream): MediaStream {
  // Новый поток заменяет старый, если тот уже умер.
  if (shared !== null && shared !== stream && !isLive(shared)) shared.getTracks().forEach((track) => track.stop());
  shared = trackCapture(stream);
  setGrantedFlag(true);
  // Система оборвала запись (звонок, Siri, блокировка). Свой `stop()`
  // событие `ended` не вызывает — значит, это не мы.
  for (const track of stream.getAudioTracks()) {
    track.addEventListener('ended', () => {
      if (shared !== stream || isLive(stream)) return;
      shared = null;
      // Звонок держит поток — открываем заново сразу; иначе — при следующем касании.
      if (holds > 0) void reopenHeld('ended')?.catch(() => undefined);
    });
    // Приглушение (iOS: прерывание аудиосессии) — в журнал; переоткрываем
    // только перед прослушиванием (`healthyShared`), чтобы не дёргать
    // микрофон, пока говорит Тико.
    track.addEventListener('mute', () => console.warn('[mic] track muted by the system'));
  }
  scheduleIdleClose();
  return stream;
}

/**
 * `getUserMedia` с правильной аудиосессией Safari. Первый запрос уходит
 * синхронно (в жесте). Если сессию между делом перевели на `playback`
 * (`InvalidStateError`), переводим обратно и повторяем один раз.
 */
function requestMic(): Promise<MediaStream> {
  prepareCaptureSession();
  let first: Promise<MediaStream>;
  try {
    first = navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
  } catch (error) {
    first = Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
  return first.catch((error: unknown) => {
    if (!isSessionConflict(error)) throw error;
    prepareCaptureSession();
    return navigator.mediaDevices.getUserMedia(MIC_CONSTRAINTS);
  });
}

/** Открыть (или вернуть уже открытый) поток. Повторных `getUserMedia` нет, пока поток жив. */
function openShared(stage: string): Promise<MediaStream> {
  const live = liveShared();
  if (live !== null) return Promise.resolve(live);
  if (opening !== null) return opening;
  const request = requestMic().then(adopt, (error: unknown) => {
    reportMicFailure(stage, error);
    if (isDenial(error)) setGrantedFlag(false);
    throw error;
  });
  opening = request;
  const clear = () => {
    if (opening === request) opening = null;
  };
  request.then(clear, clear);
  return request;
}

/**
 * Открыть микрофон сейчас, внутри жеста. Звать синхронно, первым действием
 * в обработчике касания. Поток уже открыт — ничего не делает (окна нет).
 */
export function primeMicrophone(): Promise<MediaStream> | null {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') return null;
  const request = openShared('prime');
  request.catch(() => undefined);
  return request;
}

/** Открыт или открывается ли поток прямо сейчас (без нового запроса). */
export function primedMicrophone(): Promise<MediaStream> | null {
  const live = liveShared();
  return live !== null ? Promise.resolve(live) : opening;
}

/** Поток уже открыт и жив — разрешение заведомо есть. */
export function micIsOpen(): boolean {
  return liveShared() !== null;
}

export interface MicLease {
  readonly stream: MediaStream;
  /** Вернуть поток. Дорожки не останавливаются — поток достанется следующему. */
  readonly release: () => void;
}

/** Взять поток для записи: открытый, открывающийся или новый. Ошибка — как у `getUserMedia`. */
export async function acquireMic(stage = 'stream'): Promise<MicLease> {
  const stream = await healthyShared(stage);
  leases += 1;
  window.clearTimeout(idleTimer);
  let released = false;
  return {
    stream,
    release: () => {
      if (released) return;
      released = true;
      leases = Math.max(0, leases - 1);
      scheduleIdleClose();
    },
  };
}

/**
 * Удерживать поток, не открывая его (звонок: весь разговор без повторных
 * запросов). Возвращает «отпустить».
 */
export function holdMic(): () => void {
  holds += 1;
  if (holds === 1) heldSince = Date.now();
  window.clearTimeout(idleTimer);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holds = Math.max(0, holds - 1);
    if (holds === 0) {
      heldSince = 0;
      // Звонок закончился — микрофон закрывается сразу, без минуты простоя.
      if (leases === 0) closeMic();
      return;
    }
    scheduleIdleClose();
  };
}

/** Поток сейчас держит звонок. */
export function micHeld(): boolean {
  return holds > 0;
}

/**
 * «Попробовать снова» на экране звонка: открыть микрофон заново прямо в
 * касании (iOS после прерывания пускает только так). Звать синхронно из клика.
 */
export function reopenMicInGesture(): Promise<MediaStream> | null {
  if (typeof navigator === 'undefined' || typeof navigator.mediaDevices?.getUserMedia !== 'function') return null;
  const live = liveShared();
  if (live !== null && !isMuted(live)) return Promise.resolve(live);
  if (live !== null) closeMic();
  const request = openShared('reopen-tap');
  request.then(
    () => emitHealth({ type: 'restored', reason: 'tap' }),
    () => emitHealth({ type: 'dropped', reason: 'tap' }),
  );
  return request;
}

/**
 * Системному распознаванию (Web Speech) микрофон нужен свободным: закрываем
 * поток, если его сейчас никто не держит. Разрешение к этому моменту уже выдано.
 */
export function releasePrimedMicrophone(): void {
  if (leases === 0) closeMic();
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // Во время звонка поток не закрываем: он закрывается только «Завершить»
      // и уходом с экрана звонка. iOS может сам заглушить его в фоне —
      // тогда по возвращении откроем заново.
      if (holds === 0) closeMic();
      return;
    }
    if (holds === 0) return;
    const live = liveShared();
    if (live === null || isMuted(live)) void reopenHeld('resume')?.catch(() => undefined);
  });
  window.addEventListener('pagehide', () => closeMic());
}
